/**
 * MA-PHASE11A: Create Sub-Account Modal
 * 
 * Allows agency owners/admins to create child/sub-accounts.
 */

import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export function CreateSubAccountModal({ open, onOpenChange, onSuccess }) {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast({
        title: "Validation Error",
        description: "Account name is required",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    try {
      const response = await api.post("/admin/accounts/sub-accounts", {
        name: name.trim(),
      });
      
      toast({
        title: "Success",
        description: `Sub-account "${name}" created successfully`,
      });
      
      // Reset form
      setName("");
      
      // Close modal
      onOpenChange(false);
      
      // Notify parent (for auto-switch)
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      const message = error?.response?.data?.detail || "Failed to create sub-account";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10">
        <DialogHeader>
          <DialogTitle>Create Sub-Account</DialogTitle>
          <DialogDescription className="text-slate-200/70">
            Create a new sub-account under your agency. You will become the owner of this account.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="account-name">Account Name</Label>
              <Input
                id="account-name"
                placeholder="Enter account name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
                className="bg-slate-900 border-white/10 text-slate-100"
                data-testid="sub-account-name-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !name.trim()}
              data-testid="create-sub-account-submit"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Sub-Account
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CreateSubAccountModal;
